import React from 'react';

import { FireIcon } from '@nw/shared/ui';

import { productFormsMachineEvents } from '../../../product/constants/formMachineConstants';
import {
  AssignConfigEventToContext,
  CallEventsFromConfig
} from '../../../product/machines/PublicFormsMachine/types';
import { MultiStepFormFooterProps } from '../../types/MultiStepRegistrationFormTypes';
import { MultiStepFormFooterWrapper } from '../MultiStepRegistrationForm/styledComponents';
import { MultiStepSubmitSection } from '../MultiStepSubmitSection';

import {
  AdditionalCTAButton,
  CTAAndSubmitButtonContainer,
  ErrorMessage,
  ExtraFooterUIContainer,
  LimitedSeatMessageContainer,
  LimitedSeatMessageText,
  LimitedTimeOfferContainer,
  PriceContainer,
  TnCText
} from './styledComponents';

export const MultiStepFormFooter: React.FC<MultiStepFormFooterProps> = ({
  eachFormStep,
  formApi,
  formValues,
  customClassNames,
  publicFormMachineState,
  publicFormMachineSend,
  designSystemType,
  stepId,
  nextBtnId,
  nextBtnText,
  nextBtnActions,
  onClickNextBtn,
  otpFieldRef,
  t
}) => {
  const { nextStepDetails, stepType: currentStepType, stepInfo } = eachFormStep;

  if (!nextStepDetails) {
    return null;
  }

  const handleAdditionalCTAButtonClick = (
    cta_btn_actions: string[] | undefined,
    cta_type: 'SKIP' | undefined
  ): void => {
    if (!cta_btn_actions) return;

    if (cta_type === 'SKIP') {
      //NOTE: This is a dummy email to bypass the validation, need to improve this logic
      formApi.setValue('email', 'dummy#@gmail.com');
    }

    publicFormMachineSend({
      type: productFormsMachineEvents.assignConfigEventsToContext,
      configEvent: cta_btn_actions,
      values: formValues
    } as AssignConfigEventToContext);
    publicFormMachineSend({
      type: productFormsMachineEvents.callEventsFromConfig
    } as CallEventsFromConfig);
  };

  const renderCTADetails = (): React.ReactElement | null => {
    const ctaDetails = stepInfo?.cta_details;
    if (ctaDetails && ctaDetails.length > 0) {
      return (
        <>
          {ctaDetails.map((eachCTADetails, index) => {
            if (eachCTADetails.cta_type === 'LIMITED_SEAT_AVAILABLE') {
              return (
                <LimitedSeatMessageContainer key={index}>
                  <FireIcon />
                  <LimitedSeatMessageText className={'cta-limited-seats-text'}>
                    {t('publicRegistrationForm.limitedSeatsAvailable')}
                  </LimitedSeatMessageText>
                </LimitedSeatMessageContainer>
              );
            }
            return null;
          })}
        </>
      );
    }
    return null;
  };

  const renderAdditionalCTAButton = (): React.ReactElement | null => {
    if (!stepInfo?.submit_btn_cta_details) {
      return null;
    }

    const ctaDetails = stepInfo.submit_btn_cta_details;
    const { cta_text, cta_btn_id, cta_btn_actions, cta_type } = ctaDetails;

    return (
      <AdditionalCTAButton
        id={cta_btn_id}
        onClick={() =>
          handleAdditionalCTAButtonClick(cta_btn_actions, cta_type)
        }
        className={'additionalCTAButtonClassName'}
      >
        {cta_text}
      </AdditionalCTAButton>
    );
  };

  const renderTnCText = (): React.ReactElement | null => {
    const tncText = stepInfo?.tnc_text;
    if (tncText) {
      return (
        <TnCText
          className={'termsAndConditionsTextClassName'}
          dangerouslySetInnerHTML={{
            __html: tncText
          }}
        />
      );
    }
    return null;
  };

  const renderSubmitSection = (): React.ReactElement => (
    <MultiStepSubmitSection
      currentStepType={currentStepType}
      stepId={stepId}
      nextBtnId={nextBtnId}
      nextBtnText={nextBtnText}
      nextBtnActions={nextBtnActions}
      formValues={formValues}
      customClassNames={customClassNames}
      publicFormMachineState={publicFormMachineState}
      publicFormMachineSend={publicFormMachineSend}
      designSystemType={designSystemType}
      otpFieldRef={otpFieldRef}
      onClickNextBtn={onClickNextBtn}
    />
  );

  const renderCTAAndSubmitButton = (): React.ReactElement => (
    <>
      {renderCTADetails()}
      {renderSubmitSection()}
    </>
  );

  const renderLimitedTimeOfferUI = (description: string): React.ReactElement => (
    <LimitedTimeOfferContainer
      className={customClassNames.limitedTimeOfferContainer}
    >
      <PriceContainer
        dangerouslySetInnerHTML={{
          __html: description
        }}
      />
    </LimitedTimeOfferContainer>
  );

  const renderFooterUiDetails = (): React.ReactElement => {
    const extraFooterUiDetails = stepInfo?.extra_footer_ui_details;

    if (!extraFooterUiDetails) {
      return <>{renderCTAAndSubmitButton()}</>;
    }

    const { type, description } = extraFooterUiDetails;

    if (type === 'LIMITED_TIME_OFFER_UI') {
      return (
        <ExtraFooterUIContainer>
          {renderLimitedTimeOfferUI(description)}
          <CTAAndSubmitButtonContainer>
            {renderCTAAndSubmitButton()}
          </CTAAndSubmitButtonContainer>
        </ExtraFooterUIContainer>
      );
    }

    return <>{renderCTAAndSubmitButton()}</>;
  };

  return (
    <MultiStepFormFooterWrapper
      className={customClassNames.publicRegistrationFormFooterBtnWrapper}
    >
      {renderFooterUiDetails()}
      {renderAdditionalCTAButton()}
      {renderTnCText()}
      <ErrorMessage>{publicFormMachineState.context.errorMessage}</ErrorMessage>
    </MultiStepFormFooterWrapper>
  );
};
